/**
 * pages/MechanicRequestsPage.jsx
 * 
 * Página de gestión de solicitudes de mecánica
 */

import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    MenuItem,
    ListSubheader,
    Grid,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    Alert,
    Autocomplete,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    Build as BuildIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Autorenew as InProgressIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import tw from 'twin.macro';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { AuthContext } from '../context/AuthProvider';
import {
    getAllMechanicRequests,
    getMechanicRequestById,
    getMechanicRequestStatistics,
    createMechanicRequest,
    deleteMechanicRequest,
    updateMechanicRequestStatus,
    WORK_TYPES,
    REQUEST_STATES,
    STATE_COLORS
} from '../services/mechanicRequestService';
import api from '../utils/axiosConfig';
import useRegisterPageRefresh from '../hooks/useRegisterPageRefresh';
import CicloEscolarFilter, { ALL_CYCLES_VALUE, getCicloEscolarFilterParams, getInitialCicloEscolarFilter } from '../components/CicloEscolarFilter';
import { getCicloEscolarOptionLabel } from '../services/cicloEscolarService';
import PermissionGuard from '../components/PermissionGuard';

const PageContainer = styled.div`
    ${tw`p-6`}
`;

const FiltersContainer = styled(Paper)`
    ${tw`p-4 mb-4`}
`;

const StatsCard = styled(Card)`
    ${tw`h-full`}
`;

const MechanicRequestsPage = () => {
    const { auth } = useContext(AuthContext);
    const userRole = auth?.user?.role || auth?.user?.Role?.name;

    // Estados para datos
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [schools, setSchools] = useState([]);
    const [corporations, setCorporations] = useState([]);
    const [plateOptions, setPlateOptions] = useState([]);
    const [routeOptions, setRouteOptions] = useState([]);

    // Estados para paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Estados para filtros (separados para homogeneidad con otras páginas)
    const [schoolId, setSchoolId] = useState('');
    const [corporationId, setCorporationId] = useState('');
    const [plate, setPlate] = useState('');
    const [routeNumber, setRouteNumber] = useState('');
    const [tipoTrabajo, setTipoTrabajo] = useState('');
    const [estado, setEstado] = useState('');
    const [urgente, setUrgente] = useState('');
    const [selectedCicloEscolar, setSelectedCicloEscolar] = useState(getInitialCicloEscolarFilter);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // schools alimenta tanto el picker de filtro como el picker del diálogo "Nueva Solicitud" —
    // agrupar por ciclo evita que se elija el año equivocado de un colegio con nombre repetido.
    const isSpecificCicloEscolarSelected = selectedCicloEscolar && selectedCicloEscolar !== ALL_CYCLES_VALUE;

    const groupedSchools = useMemo(() => {
        if (isSpecificCicloEscolarSelected) return [];
        const map = new Map();
        schools.forEach((s) => {
            const cycleLabel = getCicloEscolarOptionLabel(s.cicloEscolar) || (s.cicloEscolarId ? `Ciclo ${s.cicloEscolarId}` : 'Sin ciclo escolar');
            const key = cycleLabel || 'Sin ciclo escolar';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        });
        return Array.from(map.entries()).map(([cycleLabel, schools]) => ({ cycleLabel, schools }));
    }, [schools, isSpecificCicloEscolarSelected]);

    // Estados para ordenamiento
    const [sortBy, setSortBy] = useState('fechaSolicitud');
    const [sortOrder, setSortOrder] = useState('desc');

    // Estados para modales
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusNotes, setStatusNotes] = useState('');
    const [fechaCompletado, setFechaCompletado] = useState('');
    // Create modal states
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createSchoolId, setCreateSchoolId] = useState('');
    const [createCorporationId, setCreateCorporationId] = useState('');
    const [createBusId, setCreateBusId] = useState('');
    const [createPlateOptions, setCreatePlateOptions] = useState([]);
    const [createTipoTrabajo, setCreateTipoTrabajo] = useState('mecánico');
    const [createOtroTrabajoDetalle, setCreateOtroTrabajoDetalle] = useState('');
    const [createSolicitud, setCreateSolicitud] = useState('');
    const [createUrgente, setCreateUrgente] = useState(false);
    const [createLoadingPlates, setCreateLoadingPlates] = useState(false);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createError, setCreateError] = useState(null);

    // Cargar colegios y corporaciones
    useEffect(() => {
        const loadFiltersData = async () => {
            try {
                const [schoolsResponse, corpsResponse] = await Promise.all([
                    api.get('/schools', { params: { ...getCicloEscolarFilterParams(selectedCicloEscolar), includeArchived: true } }),
                    api.get('/corporations', { params: getCicloEscolarFilterParams(selectedCicloEscolar) })
                ]);
                const schoolsData = Array.isArray(schoolsResponse.data) 
                    ? schoolsResponse.data 
                    : (schoolsResponse.data?.schools || []);
                const corpsData = Array.isArray(corpsResponse.data) 
                    ? corpsResponse.data 
                    : (corpsResponse.data?.corporations || []);
                setSchools(schoolsData);
                setCorporations(corpsData);
            } catch (error) {
                console.error('Error loading filters data:', error);
            }
        };
        loadFiltersData();
    }, [selectedCicloEscolar]);

    // Cargar opciones de placas y rutas cuando cambie el cliente seleccionado
    useEffect(() => {
        const loadPlatesAndRoutes = async () => {
            try {
                // Clear previous selections
                setPlateOptions([]);
                setRouteOptions([]);

                const params = {};
                if (schoolId) params.schoolId = schoolId;
                if (corporationId) params.corporationId = corporationId;
                params.limit = 200;

                const resp = await api.get('/buses', { params });
                const buses = resp.data?.buses || [];

                const plates = buses.map(b => ({ id: b.id, label: b.plate }));
                const routeNums = Array.from(new Set(buses.map(b => b.routeNumber).filter(r => r)));

                setPlateOptions(plates);
                setRouteOptions(routeNums);
            } catch (error) {
                console.error('Error loading plate/route options:', error);
            }
        };

        // Only load when a client is selected
        if (schoolId || corporationId) {
            loadPlatesAndRoutes();
        } else {
            setPlateOptions([]);
            setRouteOptions([]);
        }
        // reset plate/route when client changes
        setPlate('');
        setRouteNumber('');
    }, [schoolId, corporationId]);

    // Función para cargar solicitudes
    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy,
                sortOrder: sortOrder.toUpperCase(),
                ...getCicloEscolarFilterParams(selectedCicloEscolar),
            };

            if (schoolId) params.schoolId = schoolId;
            if (corporationId) params.corporationId = corporationId;
            if (plate) params.plate = plate;
            if (routeNumber) params.routeNumber = routeNumber;
            if (tipoTrabajo) params.tipoTrabajo = tipoTrabajo;
            if (estado) params.estado = estado;
            if (urgente !== '') params.urgente = urgente;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await getAllMechanicRequests(params);
            setRequests(response.data || []);
            setTotalCount(response.pagination?.total || 0);
        } catch (error) {
            console.error('Error fetching mechanic requests:', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, schoolId, corporationId, plate, routeNumber, tipoTrabajo, estado, urgente, selectedCicloEscolar, startDate, endDate, sortBy, sortOrder]);

    // Función para cargar estadísticas
    const fetchStatistics = useCallback(async () => {
        try {
            const params = {
                ...getCicloEscolarFilterParams(selectedCicloEscolar),
            };
            if (schoolId) params.schoolId = schoolId;
            if (corporationId) params.corporationId = corporationId;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const stats = await getMechanicRequestStatistics(params);
            setStatistics(stats);
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    }, [schoolId, corporationId, selectedCicloEscolar, startDate, endDate]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    // Register page-level refresh handler for global refresh control
    useRegisterPageRefresh(async () => {
        await fetchRequests();
    }, [fetchRequests]);

    // Manejadores de filtros: usamos setters individuales abajo en los inputs

    // Manejadores de paginación
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Manejador de ordenamiento
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(column);
    };

    // Abrir modal de detalle
    const handleViewDetail = async (request) => {
        try {
            const detail = await getMechanicRequestById(request.id);
            setSelectedRequest(detail);
            setDetailModalOpen(true);
        } catch (error) {
            console.error('Error fetching request detail:', error);
        }
    };

    // Abrir modal de eliminar
    const handleOpenDeleteModal = (request) => {
        setRequestToDelete(request);
        setDeleteModalOpen(true);
    };

    // Confirmar eliminación
    const handleConfirmDelete = async () => {
        if (!requestToDelete) return;
        try {
            await deleteMechanicRequest(requestToDelete.id);
            setDeleteModalOpen(false);
            setRequestToDelete(null);
            fetchRequests();
            fetchStatistics();
        } catch (error) {
            console.error('Error deleting request:', error);
        }
    };

    // Abrir modal de cambio de estado
    const handleOpenStatusModal = (request) => {
        setSelectedRequest(request);
        setNewStatus(request.estado);
        setStatusNotes(request.notas || '');
        // Si ya tiene fecha de completado, usarla; sino, fecha actual
        const existingDate = request.fechaCompletado 
            ? moment(request.fechaCompletado).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD');
        setFechaCompletado(existingDate);
        setStatusModalOpen(true);
    };

    // Confirmar cambio de estado
    const handleConfirmStatusChange = async () => {
        if (!selectedRequest) return;
        try {
            const updateData = {
                estado: newStatus,
                notas: statusNotes
            };
            // Si es completado, enviar la fecha seleccionada
            if (newStatus === 'completado' && fechaCompletado) {
                updateData.fechaCompletado = fechaCompletado;
            }
            await updateMechanicRequestStatus(selectedRequest.id, updateData);
            setStatusModalOpen(false);
            setSelectedRequest(null);
            setNewStatus('');
            setStatusNotes('');
            setFechaCompletado('');
            fetchRequests();
            fetchStatistics();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    // Create modal handlers
    const handleOpenCreateModal = () => {
        // default to current selected client filters if present
        setCreateSchoolId(schoolId || '');
        setCreateCorporationId(corporationId || '');
        setCreateBusId('');
        setCreatePlateOptions([]);
        setCreateTipoTrabajo('mecánico');
        setCreateOtroTrabajoDetalle('');
        setCreateSolicitud('');
        setCreateUrgente(false);
        setCreateError(null);
        setCreateModalOpen(true);
    };

    const loadCreatePlates = async () => {
        setCreatePlateOptions([]);
        if (!createSchoolId && !createCorporationId) return;
        setCreateLoadingPlates(true);
        try {
            const params = { limit: 200 };
            if (createSchoolId) params.schoolId = createSchoolId;
            if (createCorporationId) params.corporationId = createCorporationId;
            const resp = await api.get('/buses', { params });
            const buses = resp.data?.buses || [];
            const plates = buses.map(b => ({ id: b.id, label: b.plate }));
            setCreatePlateOptions(plates);
        } catch (e) {
            console.error('Error loading create plates:', e);
        } finally {
            setCreateLoadingPlates(false);
        }
    };

    useEffect(() => {
        loadCreatePlates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createSchoolId, createCorporationId]);

    const handleConfirmCreate = async () => {
        setCreateError(null);
        if (!createBusId) {
            setCreateError('Seleccione un bus válido.');
            return;
        }
        if (!createSolicitud || String(createSolicitud).trim() === '') {
            setCreateError('La descripción de la solicitud es requerida.');
            return;
        }
        setCreateSubmitting(true);
        try {
            const payload = {
                busId: createBusId,
                tipoTrabajo: createTipoTrabajo,
                otroTrabajoDetalle: createOtroTrabajoDetalle || null,
                solicitud: createSolicitud,
                urgente: !!createUrgente
            };
            await createMechanicRequest(payload);
            setCreateModalOpen(false);
            setCreateSubmitting(false);
            fetchRequests();
            fetchStatistics();
        } catch (e) {
            console.error('Error creating mechanic request:', e);
            setCreateError(e?.response?.data?.message || 'Error al crear la solicitud.');
            setCreateSubmitting(false);
        }
    };

    // Obtener icono de estado
    const getStatusIcon = (estado) => {
        switch (estado) {
            case 'pendiente':
                return <PendingIcon fontSize="small" />;
            case 'en_proceso':
                return <InProgressIcon fontSize="small" />;
            case 'completado':
                return <CheckCircleIcon fontSize="small" />;
            case 'cancelado':
                return <CancelIcon fontSize="small" />;
            default:
                return null;
        }
    };

    // Formatear fecha con hora
    const formatDate = (date) => {
        if (!date) return '-';
        return moment(date).tz('America/Guatemala').format('DD/MM/YYYY HH:mm');
    };

    // Formatear solo fecha (sin hora)
    const formatDateOnly = (date) => {
        if (!date) return '-';
        return moment(date).tz('America/Guatemala').format('DD/MM/YYYY');
    };

    // Columnas ordenables
    const sortableColumns = [
        { id: 'fechaSolicitud', label: 'Fecha' },
        { id: 'tipoTrabajo', label: 'Tipo de Trabajo' },
        { id: 'estado', label: 'Estado' },
        { id: 'urgente', label: 'Urgente' }
    ];

    return (
        <PageContainer>
            <Typography variant="h4" gutterBottom>
                <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Solicitudes de Mecánica
            </Typography>

            {/* Estadísticas */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total
                                </Typography>
                                <Typography variant="h4">
                                    {statistics.totalRequests}
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard sx={{ borderLeft: '4px solid #ff9800' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Urgentes
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {statistics.urgentRequests}
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard sx={{ borderLeft: '4px solid #2196f3' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Pendientes
                                </Typography>
                                <Typography variant="h4" color="info.main">
                                    {statistics.pendingRequests}
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard sx={{ borderLeft: '4px solid #9c27b0' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    En Proceso
                                </Typography>
                                <Typography variant="h4" color="secondary.main">
                                    {statistics.inProgressRequests}
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard sx={{ borderLeft: '4px solid #4caf50' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Completados
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {statistics.completedRequests}
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatsCard>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Más Solicitudes
                                </Typography>
                                <Typography variant="body1" noWrap>
                                    {statistics.topSchool?.name || statistics.topCorporation?.name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {statistics.topSchool?.count || statistics.topCorporation?.count || 0} solicitudes
                                </Typography>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                </Grid>
            )}

            {/* Filtros */}
            <FiltersContainer elevation={1}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <CicloEscolarFilter
                            value={selectedCicloEscolar}
                            onChange={(value) => {
                                setSelectedCicloEscolar(value);
                                setSchoolId('');
                                setCorporationId('');
                                setPlate('');
                                setRouteNumber('');
                                setPage(0);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Colegio"
                            value={schoolId}
                            onChange={(e) => { setSchoolId(e.target.value); setCorporationId(''); setPage(0); setPlate(''); setRouteNumber(''); }}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                            disabled={!!corporationId}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {isSpecificCicloEscolarSelected || groupedSchools.length === 0
                                ? schools.map((school) => (
                                    <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                                ))
                                : groupedSchools.map((group) => [
                                    <ListSubheader key={`header-${group.cycleLabel}`} disableSticky sx={{ lineHeight: 1.6, color: '#1976d2', fontWeight: 800, bgcolor: 'background.paper' }}>
                                        {group.cycleLabel}
                                    </ListSubheader>,
                                    ...group.schools.map((school) => (
                                        <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                                    ))
                                ])}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Corporación"
                            value={corporationId}
                            onChange={(e) => { setCorporationId(e.target.value); setSchoolId(''); setPage(0); setPlate(''); setRouteNumber(''); }}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                            disabled={!!schoolId}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {corporations.map((corp) => (
                                <MenuItem key={corp.id} value={corp.id}>
                                    {corp.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Placa"
                            value={plate}
                            onChange={(e) => { setPlate(e.target.value); setPage(0); }}
                            disabled={!schoolId && !corporationId}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {[...new Set(plateOptions.map(p => p.label))].map((pl) => (
                                <MenuItem key={pl} value={pl}>{pl}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Ruta"
                            value={routeNumber}
                            onChange={(e) => { setRouteNumber(e.target.value); setPage(0); }}
                            disabled={!schoolId && !corporationId}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {routeOptions.map((r) => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Tipo Trabajo"
                            value={tipoTrabajo}
                            onChange={(e) => { setTipoTrabajo(e.target.value); setPage(0); }}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.entries(WORK_TYPES).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Estado"
                            value={estado}
                            onChange={(e) => { setEstado(e.target.value); setPage(0); }}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.entries(REQUEST_STATES).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={0.8}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Urgente"
                            value={urgente}
                            onChange={(e) => { setUrgente(e.target.value); setPage(0); }}
                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="true">Sí</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.25}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Fecha Inicio"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.25}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Fecha Fin"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.25}>
                        <PermissionGuard permission="mecanica-crear-solicitud">
                            <Button variant="contained" color="primary" onClick={handleOpenCreateModal} sx={{ mr: 1 }}>
                                Nueva Solicitud
                            </Button>
                        </PermissionGuard>
                    </Grid>
                </Grid>
            </FiltersContainer>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Table size="small" sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'fechaSolicitud'}
                                            direction={sortBy === 'fechaSolicitud' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('fechaSolicitud')}
                                        >
                                            Fecha
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'tipoTrabajo'}
                                            direction={sortBy === 'tipoTrabajo' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('tipoTrabajo')}
                                        >
                                            Tipo de Trabajo
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Solicitud</TableCell>
                                    <TableCell>Placa</TableCell>
                                    <TableCell>Ruta</TableCell>
                                    <TableCell>Colegio/Corp.</TableCell>
                                    <TableCell>Solicitante</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'urgente'}
                                            direction={sortBy === 'urgente' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('urgente')}
                                        >
                                            Urgente
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'estado'}
                                            direction={sortBy === 'estado' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('estado')}
                                        >
                                            Estado
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No se encontraron solicitudes de mecánica
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    requests.map((request) => (
                                        <TableRow key={request.id} hover>
                                            <TableCell>
                                                {formatDate(request.fechaSolicitud)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={WORK_TYPES[request.tipoTrabajo] || request.tipoTrabajo}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={request.solicitud}>
                                                    <Typography noWrap sx={{ maxWidth: 200 }}>
                                                        {request.solicitud}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {request.bus?.plate || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {request.routeNumber || request.bus?.routeNumber || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {request.school?.name || request.corporation?.name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {request.supervisorId 
                                                    ? `${request.supervisor?.name || '-'} (Supervisor)`
                                                    : `${request.pilot?.name || '-'} (Piloto)`}
                                            </TableCell>
                                            <TableCell>
                                                {request.urgente ? (
                                                    <Chip
                                                        icon={<WarningIcon />}
                                                        label="Sí"
                                                        size="small"
                                                        color="error"
                                                    />
                                                ) : (
                                                    <Chip label="No" size="small" variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(request.estado)}
                                                    label={REQUEST_STATES[request.estado] || request.estado}
                                                    size="small"
                                                    color={STATE_COLORS[request.estado] || 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Ver Detalle">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetail(request)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <PermissionGuard permission="mecanica-actualizar-estado">
                                                    <Tooltip title="Cambiar Estado">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenStatusModal(request)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionGuard>
                                                <PermissionGuard permission="mecanica-eliminar-solicitud">
                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteModal(request)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionGuard>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            labelRowsPerPage="Filas por página"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                            }
                        />
                    </>
                )}
            </TableContainer>

            {/* Modal de Crear Solicitud */}
            <Dialog
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Nueva Solicitud de Mecánica
                </DialogTitle>
                <DialogContent dividers>
                    {createError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Colegio"
                                    value={createSchoolId}
                                    onChange={(e) => { setCreateSchoolId(e.target.value); setCreateCorporationId(''); setCreateBusId(''); }}
                                    disabled={!!createCorporationId}
                                >
                                <MenuItem value="">Seleccione</MenuItem>
                                {isSpecificCicloEscolarSelected || groupedSchools.length === 0
                                    ? schools.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))
                                    : groupedSchools.map((group) => [
                                        <ListSubheader key={`header-create-${group.cycleLabel}`} disableSticky sx={{ lineHeight: 1.6, color: '#1976d2', fontWeight: 800, bgcolor: 'background.paper' }}>
                                            {group.cycleLabel}
                                        </ListSubheader>,
                                        ...group.schools.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                        ))
                                    ])}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Corporación"
                                    value={createCorporationId}
                                    onChange={(e) => { setCreateCorporationId(e.target.value); setCreateSchoolId(''); setCreateBusId(''); }}
                                    disabled={!!createSchoolId}
                                >
                                <MenuItem value="">Seleccione</MenuItem>
                                {corporations.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Placa/Bus"
                                value={createBusId}
                                onChange={(e) => setCreateBusId(e.target.value)}
                                disabled={(!createSchoolId && !createCorporationId) || createLoadingPlates}
                            >
                                <MenuItem value="">Seleccione</MenuItem>
                                {createPlateOptions.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Tipo Trabajo"
                                value={createTipoTrabajo}
                                onChange={(e) => setCreateTipoTrabajo(e.target.value)}
                            >
                                {Object.entries(WORK_TYPES).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {createTipoTrabajo === 'otro' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Detalle de Otro Trabajo"
                                    value={createOtroTrabajoDetalle}
                                    onChange={(e) => setCreateOtroTrabajoDetalle(e.target.value)}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Solicitud"
                                value={createSolicitud}
                                onChange={(e) => setCreateSolicitud(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Urgente</InputLabel>
                                <Select
                                    value={createUrgente ? 'true' : 'false'}
                                    onChange={(e) => setCreateUrgente(e.target.value === 'true')}
                                    label="Urgente"
                                >
                                    <MenuItem value="false">No</MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateModalOpen(false)} disabled={createSubmitting}>Cancelar</Button>
                    <Button variant="contained" onClick={handleConfirmCreate} disabled={createSubmitting}>
                        {createSubmitting ? 'Guardando...' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Detalle */}
            <Dialog
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Detalle de Solicitud de Mecánica
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Fecha de Solicitud
                                </Typography>
                                <Typography>
                                    {formatDate(selectedRequest.fechaSolicitud)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Estado
                                </Typography>
                                <Chip
                                    icon={getStatusIcon(selectedRequest.estado)}
                                    label={REQUEST_STATES[selectedRequest.estado] || selectedRequest.estado}
                                    color={STATE_COLORS[selectedRequest.estado] || 'default'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Tipo de Trabajo
                                </Typography>
                                <Typography>
                                    {WORK_TYPES[selectedRequest.tipoTrabajo] || selectedRequest.tipoTrabajo}
                                    {selectedRequest.tipoTrabajo === 'otro' && selectedRequest.otroTrabajoDetalle && (
                                        <span> - {selectedRequest.otroTrabajoDetalle}</span>
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Urgente
                                </Typography>
                                {selectedRequest.urgente ? (
                                    <Chip icon={<WarningIcon />} label="Sí" size="small" color="error" />
                                ) : (
                                    <Chip label="No" size="small" variant="outlined" />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Solicitud
                                </Typography>
                                <Typography>{selectedRequest.solicitud}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Bus
                                </Typography>
                                <Typography>
                                    {selectedRequest.bus?.plate || '-'}
                                    {selectedRequest.bus?.description && ` - ${selectedRequest.bus.description}`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Ruta
                                </Typography>
                                <Typography>
                                    {selectedRequest.routeNumber || selectedRequest.bus?.routeNumber || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Colegio
                                </Typography>
                                <Typography>{selectedRequest.school?.name || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Corporación
                                </Typography>
                                <Typography>{selectedRequest.corporation?.name || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Piloto del Bus
                                </Typography>
                                <Typography>
                                    {selectedRequest.pilot?.name || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Solicitante
                                </Typography>
                                <Typography>
                                    {selectedRequest.supervisorId 
                                        ? `${selectedRequest.supervisor?.name || '-'} (Supervisor)`
                                        : `${selectedRequest.pilot?.name || '-'} (Piloto)`}
                                </Typography>
                            </Grid>
                            {selectedRequest.fechaCompletado && (
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Fecha de Completado
                                    </Typography>
                                    <Typography>
                                        {formatDateOnly(selectedRequest.fechaCompletado)}
                                    </Typography>
                                </Grid>
                            )}
                            {selectedRequest.notas && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Notas
                                    </Typography>
                                    <Typography>{selectedRequest.notas}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailModalOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Cambio de Estado */}
            <Dialog
                open={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Cambiar Estado de Solicitud
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Estado</InputLabel>
                                <Select
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                        label="Estado"
                                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                                    >
                                    {Object.entries(REQUEST_STATES).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notas"
                                value={statusNotes}
                                onChange={(e) => setStatusNotes(e.target.value)}
                                placeholder="Agregar notas sobre el cambio de estado..."
                            />
                        </Grid>
                        {newStatus === 'completado' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Fecha de Completado"
                                    value={fechaCompletado}
                                    onChange={(e) => setFechaCompletado(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusModalOpen(false)}>Cancelar</Button>
                    <PermissionGuard permission="mecanica-actualizar-estado">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleConfirmStatusChange}
                        >
                            Guardar
                        </Button>
                    </PermissionGuard>
                </DialogActions>
            </Dialog>

            {/* Modal de Confirmación de Eliminación */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
            >
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Está seguro de que desea eliminar esta solicitud de mecánica?
                    </Typography>
                    {requestToDelete && (
                        <Box mt={2}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Bus:</strong> {requestToDelete.bus?.plate || '-'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Tipo:</strong> {WORK_TYPES[requestToDelete.tipoTrabajo] || requestToDelete.tipoTrabajo}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Fecha:</strong> {formatDate(requestToDelete.fechaSolicitud)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default MechanicRequestsPage;
